import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Download, Megaphone, Facebook, Search } from "lucide-react";
import { useContentDefaults } from "@/components/content/DraftBuilderModal";

export const downloadPPC = async (id, fmt) => {
  try {
    const r = await api.get(`/ppc/${id}/export?fmt=${fmt}`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = `${fmt === "meta" ? "meta-ads" : "google-ads-editor"}-${id.slice(0, 6)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  } catch (e) { toast.error("Export failed"); }
};

export const PPCPlanView = ({ doc }) => {
  const g = doc.plan.google, m = doc.plan.meta;
  return (
    <div className="space-y-4" data-testid={`ppc-plan-${doc.id}`}>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="bg-primary hover:bg-primary/90 text-white" onClick={() => downloadPPC(doc.id, "google")} data-testid={`ppc-export-google-${doc.id}`}><Download className="h-3.5 w-3.5 mr-1.5" /> Google Ads Editor CSV</Button>
        <Button size="sm" variant="outline" className="border-border" onClick={() => downloadPPC(doc.id, "meta")} data-testid={`ppc-export-meta-${doc.id}`}><Download className="h-3.5 w-3.5 mr-1.5" /> Meta Ads Manager CSV</Button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <div className="rounded-lg border border-border bg-secondary/20 p-3">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Search className="h-4 w-4 text-primary" /> Google Search · ${g.daily_budget}/day · ~${g.avg_cpc} CPC</p>
          {g.ad_groups.map((ag) => (
            <div key={ag.name} className="mt-3 text-xs space-y-1" data-testid="ppc-ad-group">
              <p className="font-semibold">{ag.name} <span className="text-muted-foreground font-normal">· {ag.keywords.length} keywords · max CPC ${ag.max_cpc}</span></p>
              <p className="text-muted-foreground">{ag.keywords.join(" · ")}</p>
              <p className="text-[#8ab4f8]">{ag.headlines.slice(0, 3).join(" | ")}</p>
              <p>{ag.descriptions[0]}</p>
            </div>
          ))}
          {g.negative_keywords?.length > 0 && <p className="mt-3 text-[11px] text-muted-foreground">Negatives: {g.negative_keywords.join(", ")}</p>}
        </div>
        <div className="rounded-lg border border-border bg-secondary/20 p-3">
          <p className="text-sm font-semibold flex items-center gap-1.5"><Facebook className="h-4 w-4 text-[#6fa3ff]" /> Meta · {m.ad_sets.length} ad sets · ${m.daily_budget}/day</p>
          {m.ad_sets.map((s) => (
            <div key={s.name} className="mt-3 text-xs space-y-1" data-testid="ppc-ad-set">
              <p className="font-semibold">{s.name} <span className="text-muted-foreground font-normal">· age {s.age_min}-{s.age_max} · ${s.daily_budget}/day</span></p>
              <p className="text-muted-foreground">{s.interests.join(", ")}</p>
              {s.ads.slice(0, 1).map((a) => <p key={a.headline}><span className="font-medium">{a.headline}</span> — {a.primary_text}</p>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default function PPCModal({ open, onOpenChange, keywords, metrics, workspace, onUpgrade }) {
  const defaults = useContentDefaults(open ? workspace?.id : null);
  const [target, setTarget] = useState("");
  const [finalUrl, setFinalUrl] = useState("");
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);
  const [doc, setDoc] = useState(null);
  useEffect(() => { if (open) setDoc(null); }, [open]);
  useEffect(() => { if (defaults && !target) { setTarget(defaults.target_url || ""); setFinalUrl(defaults.target_url || ""); } }, [defaults]); // eslint-disable-line

  const run = async () => {
    if (!target.trim()) { toast.error("Add your website URL"); return; }
    setBusy(true);
    try {
      const r = await api.post("/ppc/generate", { workspace_id: workspace.id, keywords, target_url: target, final_url: finalUrl || target, daily_budget: Number(budget) || 0, metrics });
      setDoc(r.data);
      toast.success("PPC campaign built — also added to your calendar");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if ([402, 403].includes(e?.response?.status) && d?.message) onUpgrade?.({ kind: "ppc", message: d.message });
      else toast.error(typeof d === "string" ? d : (d?.message || "Could not build the campaign"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-card border-border max-h-[92vh] overflow-y-auto" data-testid="ppc-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><Megaphone className="h-5 w-5 text-primary" /> Build a PPC campaign</DialogTitle>
          <DialogDescription>Google Search ad groups + Meta ad sets from {keywords.length} keywords. Exported as import-ready files — nothing is spent from AutoGridSocial.</DialogDescription>
        </DialogHeader>
        {!doc ? (
          <div className="space-y-3">
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label className="text-xs">Website</Label><Input value={target} onChange={(e) => setTarget(e.target.value)} className="mt-1 bg-background" data-testid="ppc-target-url" /></div>
              <div><Label className="text-xs">Landing page (final URL)</Label><Input value={finalUrl} onChange={(e) => setFinalUrl(e.target.value)} className="mt-1 bg-background" data-testid="ppc-final-url" /></div>
              <div><Label className="text-xs">Daily budget (USD, optional)</Label><Input type="number" min="0" value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="auto from CPC" className="mt-1 bg-background" data-testid="ppc-budget" /></div>
            </div>
            <Button onClick={run} disabled={busy} className="w-full h-11 bg-primary hover:bg-primary/90 text-white" data-testid="ppc-generate-btn">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Building ad groups and ad sets…</> : "Build Google + Meta campaign"}
            </Button>
          </div>
        ) : <PPCPlanView doc={doc} />}
      </DialogContent>
    </Dialog>
  );
}
